import { Global } from '@emotion/react'
import {
  Add,
  Assessment,
  Delete,
  ExitToApp,
  Pause,
  PlayArrow,
  QueueMusic,
  VolumeUp,
} from '@mui/icons-material'
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon'
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined'
import {
  AppBar,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Divider,
  IconButton,
  Paper,
  Slider,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import Modal from '@mui/material/Modal'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import { grey } from '@mui/material/colors'
import { styled } from '@mui/material/styles'
import EmojiPicker from 'emoji-picker-react'
import { CheckIcon } from 'lucide-react'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import YouTube from 'react-youtube'
import '../App.css'
import { useChatSocket } from '../hooks/useChatSocket'
import { getTokenData } from '../utils/ProtectedRoute'
import socket from '../utils/socket'
import DraggableQueue from './DraggableQueue'
import SearchSongsDialog from './SearchSongsDialog'

const token = localStorage.getItem('token')
const BACKEND_URL = process.env.REACT_APP_SERVER_SOCKET

export default function YouTubePlayerUI({
  logout,
  onPlayerReady,
  onPlayerStateChange,
  handleClick,
  handleVolumeChange,
  handleAddVideo,
  handlePlayQueueItem,
  handleDeleteVideo,
  videoId,
  isPlaying,
  isMuted,
  volume,
  newVideoInput,
  setNewVideoInput,
  queue,
  thumbnailImage,
  setThumbnailImage,
  setQueue,
  playerRef,
  currentTime,
  setCurrentTime,
  seeking,
  setSeeking,
  duration,
  setDuration,
  roomId,
  handlePlayPauseClick,
  handleSeek,
}) {
  const navigate = useNavigate()
  const { role, email } = getTokenData()
  const [open, setOpen] = useState(false)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [copy, setCopy] = useState(false)
  const profile = localStorage.getItem('profile') || ''
  const [chatInput, setChatInput] = useState('')
  const [showEmoji, setEmoji] = useState(false)
  const messageRef = useRef(null)
  const [isTypingUser, setIsTypingUser] = useState(null)
  const [typingUsers, setTypingUsers] = useState([])
  // search songs dialogs props
  const [openSearchSongsDialog, setOpenSearchSongsDialog] = useState(false)
  // chat states
  const { sendMessage, onlineUsers, messages } = useChatSocket(roomId, email)
  const handleSend = (emoji) => {
    setEmoji(false)
    if (chatInput.trim()) {
      if (emoji?.emoji) {
        sendMessage(chatInput.trim() + ' ' + emoji.emoji)
      } else {
        sendMessage(chatInput.trim())
      }
      setChatInput('')
    }
  }

  // handle message change with emoji
  const handleMessageChange = (e) => {
    setEmoji(false)
    if (e?.emoji) {
      setChatInput((prev) => prev + ' ' + e?.emoji)
    } else {
      setChatInput(e.target.value)
    }
    socket.emit('typing', { roomId, email })

    if (isTypingUser) clearTimeout(isTypingUser)

    const timeout = setTimeout(() => {
      socket.emit('stopTyping', { roomId, email })
    }, 2000)

    setIsTypingUser(timeout)
  }

  useEffect(() => {
    socket.on('typing', ({ email }) => {
      setTypingUsers((prev) => {
        if (!prev.includes(email)) return [...prev, email]
        return prev
      })
    })

    socket.on('stopTyping', ({ email }) => {
      setTypingUsers((prev) => prev.filter((e) => e !== email))
    })

    return () => {
      socket.off('typing')
      socket.off('stopTyping')
    }
  }, [])

  const handleOpenSearchSongsDialog = () => setOpenSearchSongsDialog(true)

  const handleCloseSearchSongsDialog = () => {
    setOpenSearchSongsDialog(false)
    setQuery('')
    setDebouncedQuery('')
  }
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const opts = {
    height: '0',
    width: '0',
    playerVars: {
      autoplay: 1,
      controls: 0,
      modestbranding: 1,
      rel: 0,
      origin: window.location.origin,
    },
  }
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`
  }

  useEffect(() => {
    if (messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [messages, chatInput])

  // Room info for subheader
  const [roomName, setRoomName] = useState('')
  useEffect(() => {
    if (!roomId) return
    // Try to fetch room name from backend or localStorage
    fetch(`${BACKEND_URL}/api/room/validate/${roomId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userEmail: email }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.valid && data.roomName) setRoomName(data.roomName)
        else setRoomName('')
      })
      .catch(() => setRoomName(''))
  }, [roomId, email])

  const handleCopyRoomId = () => {
    navigator.clipboard.writeText(roomId)
    setCopy(true)
    setTimeout(() => {
      setCopy(false)
    }, 3000)
  }
  const handleExitRoom = () => {
    localStorage.removeItem('roomId')
    window.location.reload()
  }

  const formatTimestamp = (timestamp) => {
    const now = moment()
    const msgTime = moment(timestamp)

    if (now.isSame(msgTime, 'day')) {
      return msgTime.format('LT')
    } else if (now.subtract(1, 'day').isSame(msgTime, 'day')) {
      return `Yesterday, ${msgTime.format('LT')}`
    } else {
      return msgTime.format('MMM D, LT')
    }
  }

  const isQueueEmpty = !queue || queue.length === 0
  const otherUsers = onlineUsers.filter((u) => u.email !== email)
  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#121212', // Spotify's main background
          // backgroundImage: isQueueEmpty
          //   ? 'none'
          //   : `url(${thumbnailImage.songThumbnail})`,
          // backgroundRepeat: 'no-repeat',
          // backgroundSize: isQueueEmpty ? 'auto' : isMobile ? 'cover' : 'cover',
          // backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
        }}
        className='youtube-player-container'
      >
        {/* Header */}
        <AppBar
          position='static'
          elevation={0}
          sx={{
            bgcolor: '#000000', // Spotify's header background
            color: 'text.primary',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.3)',
          }}
        >
          <Toolbar
            sx={{
              margin: { xs: '8px', sm: '10px' },
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 },
            }}
          >
            {/* Left: Version and logo */}
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'center', sm: 'end' },
                justifyContent: 'center',
                flexDirection: { xs: 'row', sm: 'column' },
                gap: 1,
                order: isMobile && 1,
              }}
            >
              <img
                alt='logo'
                src='./logo.png'
                style={{
                  height: isMobile ? '40px' : '50px',
                  width: 'auto',
                }}
              />
              <Typography
                sx={{
                  color: 'gray',
                  fontSize: { xs: '0.7rem', sm: '0.8rem', md: '10px' },
                }}
                variant='body2'
              >
                V11.07.04
              </Typography>
            </Box>

            {/* search bar */}
            <Box
              sx={{
                flexGrow: 1,
                maxWidth: { xs: '100%', sm: '350px', md: '450px' },
                px: { xs: 0, sm: 1 },
                order: isMobile && 3,
                mt: isMobile && 1,
              }}
            >
              <TextField
                placeholder='Search'
                fullWidth={true}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    backgroundColor: '#282828',
                    borderRadius: '4px',
                    '& fieldset': { borderColor: '#404040' },
                    '&:hover fieldset': { borderColor: '#1DB954' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1DB954',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputBase-input': { padding: '8px 12px' },
                }}
                onKeyDown={(e) => {
                  if (e.nativeEvent.key.toLowerCase() === 'enter') {
                    e.preventDefault()
                    handleOpenSearchSongsDialog()
                  }
                }}
              />
              {openSearchSongsDialog && (
                <SearchSongsDialog
                  openSearchSongsDialog={openSearchSongsDialog}
                  handleCloseSearchSongsDialog={handleCloseSearchSongsDialog}
                  handleAddVideo={handleAddVideo}
                  setNewVideoInput={setNewVideoInput}
                  query={query}
                  setQuery={setQuery}
                  debouncedQuery={debouncedQuery}
                  setDebouncedQuery={setDebouncedQuery}
                />
              )}
            </Box>

            {/* Right: Buttons */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
                gap: { xs: 1, sm: 2 },
                flexWrap: 'wrap',
                order: isMobile && 2,
              }}
            >
              {role === 'admin' && !isMobile && (
                <Button
                  variant='outlined'
                  startIcon={<Assessment />}
                  onClick={() => navigate('/activity-log')}
                  sx={{
                    border: '1px solid rgb(242, 242, 242)',
                    color: '#fff',
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    padding: { xs: '4px 8px', sm: '6px 12px' },
                    '&:hover': {
                      border: '1px solid #fff',
                      bgcolor: 'rgba(241, 242, 242, 0.3)',
                      boxShadow: '1px 1px 8px 6px rgba(241, 242, 242, 0.3)',
                    },
                    mr: 1,
                  }}
                >
                  Activity Log
                </Button>
              )}
              <Stack direction={'row'}>
                {profile && (
                  <Avatar
                    src={profile}
                    alt={email.split('.')[0].charAt(0).toUpperCase()}
                    title={email.split('.')[0]}
                  />
                )}
                <Button
                  startIcon={<ExitToApp />}
                  onClick={logout}
                  variant='outlined'
                  sx={{
                    border: '1px solid rgb(255, 44, 44)',
                    color: '#ff0000',
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    padding: { xs: '4px 8px', sm: '6px 12px' },
                    '&:hover': {
                      fontWeight: 'bold',
                      border: '1px solid #ff0000',
                      bgcolor: 'rgba(231, 13, 13, 0.3)',
                      boxShadow: '1px 1px 8px 8px rgba(231, 13, 13, 0.3)',
                    },
                    ml: '10px',
                  }}
                  color='error'
                  size={isMobile ? 'small' : 'medium'}
                >
                  Logout
                </Button>
              </Stack>
            </Box>
          </Toolbar>

          {/* Subheader for room info */}
          {roomId && (
            <Box
              sx={{
                bgcolor: '#1a1a1a',
                px: { xs: 2, sm: 3 },
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Box>
                <Typography
                  color='gray'
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
                  variant='body2'
                >
                  Room Name: {roomName}
                </Typography>
                <Typography
                  variant='caption'
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                  color='gray'
                >
                  Room ID: {roomId}
                  {!isMobile && (
                    <Button
                      size='small'
                      onClick={handleCopyRoomId}
                      sx={{
                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        padding: { xs: '2px 6px', sm: '4px 8px' },
                        color: 'white',
                        border: '1px solid #fff',
                        bgcolor: copy && 'green',
                        ml: 1,
                      }}
                    >
                      {copy ? <CheckIcon /> : 'Copy'}
                    </Button>
                  )}
                </Typography>
              </Box>
              <Button
                size='small'
                color='error'
                variant='outlined'
                onClick={handleExitRoom}
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  padding: { xs: '2px 6px', sm: '4px 8px' },
                  '&:hover': {
                    fontWeight: 'bold',
                    border: '1px solid #ff0000',
                    bgcolor: 'rgba(231, 13, 13, 0.3)',
                    boxShadow: '1px 1px 8px 8px rgba(231, 13, 13, 0.3)',
                  },
                }}
              >
                Exit Room
              </Button>
            </Box>
          )}
        </AppBar>

        {/* chat and video paper */}
        <Paper
          sx={{
            width: '100%',
            maxWidth: 1200,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'center',
            // alignItems: 'stretch', // Changed to stretch for full height
            gap: 3,
            mb: 3,
            p: 2,
            bgcolor: '#181818',
            borderRadius: 2,
            boxShadow: 4,
            margin: '20px auto',
            minHeight: 500, // Set minimum height for the container
          }}
        >
          {/* YouTube Video Section */}
          <Box
            sx={{
              flex: 1, // This will take all available space
              minWidth: 0, // Allow flex item to shrink below content size
              bgcolor: '#000',
              borderRadius: 2,
              position: 'relative', // For absolute positioning of YouTube component
            }}
          >
            {videoId ? (
              // <YouTube
              //   videoId={videoId}
              //   opts={{
              //     ...opts,
              //     width: '100%',
              //     height: '100%',
              //     playerVars: {
              //       ...opts.playerVars,
              //       autoplay: 0,
              //       controls: 1,
              //     },
              //   }}
              //   style={{
              //     width: '100%',
              //     height: '100%',
              //     position: 'absolute',
              //     top: 0,
              //     left: 0,
              //   }}
              // />
              <img
                src={thumbnailImage.songThumbnail}
                width={'100%'}
                height={'100%'}
                alt='song thumbnail'
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography color='white'>No Song selected</Typography>
              </Box>
            )}
          </Box>

          {/* Chat Section */}
          <Stack direction={'column'} flexGrow={isMobile && 1}>
            {/* chat online members */}
            <Box
              sx={{
                // height: '80px',
                widht: '200px',
                border: '2px solid rgb(53, 53, 53)',
                mb: '8px',
                color: 'white',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                p: '12px',
              }}
            >
              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                // p={'10px'}
              >
                <Typography color='white' variant='h5'>
                  Members ({otherUsers.length})
                </Typography>
                <PeopleOutlineOutlinedIcon />
              </Stack>
              <AvatarGroup
                total={otherUsers.length}
                sx={{
                  alignSelf: 'flex-start',
                  pt: '5px',
                  '& .MuiAvatar-root': {
                    height: 25,
                    width: 25,
                    fontSize: '1rem',
                  },
                }}
              >
                {onlineUsers.map(
                  (e, i) =>
                    e.email !== email && (
                      <Avatar
                        key={i}
                        sx={{ height: '25px', width: '25px' }}
                        alt={onlineUsers[i].email.charAt(0).toUpperCase()}
                        src={'/static/images/avatar/1.jpg'}
                        title={e.email.split('.')[0]}
                      />
                    )
                )}
              </AvatarGroup>
            </Box>
            <Box
              sx={{
                width: isMobile ? '100%' : 320,
                minWidth: 220,
                height: isMobile ? '550px' : '480px',
                bgcolor: '#23272f',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                boxShadow: 2,
                flexShrink: 0,
                mb: isMobile && '280px',
              }}
            >
              <Typography color='white' variant='h6' sx={{ mb: 1 }}>
                Chat
              </Typography>

              {/* Scrollable Chat Box */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  scrollBehavior: 'smooth',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                {messages.map((m, idx) => {
                  const mine = m.email === email
                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: mine ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Typography
                        variant='caption'
                        sx={{
                          color: mine ? '#1DB954' : '#b3b3b3',
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        {mine ? 'You' : m.email.split('.')[0]}
                      </Typography>

                      <Box
                        sx={{
                          bgcolor: mine ? '#1DB954' : '#121212',
                          color: '#fff',
                          borderRadius: mine
                            ? '16px 16px 4px 16px'
                            : '16px 16px 16px 4px',
                          px: 2,
                          py: 1,
                          maxWidth: '80%',
                          boxShadow: 2,
                        }}
                      >
                        <Typography
                          variant='body2'
                          sx={{ wordBreak: 'break-word' }}
                        >
                          {m.message}
                        </Typography>
                      </Box>

                      <Typography
                        variant='caption'
                        sx={{ color: '#888', fontSize: '0.7em', mt: 0.5 }}
                      >
                        {formatTimestamp(m?.timestamp)}
                      </Typography>
                    </Box>
                  )
                })}

                {/* Typing indicator */}
                {typingUsers.length > 0 && (
                  <Typography variant='caption' color='green' sx={{ mt: 1 }}>
                    {typingUsers
                      .map((u) => u.split('.')[0].toUpperCase())
                      .join(', ')}{' '}
                    {typingUsers.length === 1 ? 'is' : 'are'} typing...
                  </Typography>
                )}
                {/* scroll anchor */}
                <div ref={messageRef} />
              </Box>

              {/* Divider & Input */}
              <Divider sx={{ bgcolor: 'gray', width: '100%', mt: 1 }} />
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  gap: 1,
                  mt: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TextField
                  size='small'
                  fullWidth
                  placeholder='Type a message...'
                  value={chatInput}
                  onChange={handleMessageChange}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      backgroundColor: '#282828',
                      borderRadius: '20px',
                      '& fieldset': { borderColor: '#404040' },
                      '&:hover fieldset': { borderColor: '#1DB954' },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1DB954',
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': { padding: '10px 16px' },
                  }}
                />
                <Button
                  variant='contained'
                  color='success'
                  sx={{
                    borderRadius: '20px',
                    px: 3,
                    border: !chatInput && '2px solid gray',
                  }}
                  onClick={handleSend}
                  disabled={!chatInput.trim()}
                >
                  Send
                </Button>
                <InsertEmoticonIcon
                  sx={{ color: 'gray', cursor: 'pointer' }}
                  onClick={() => setEmoji((val) => !val)}
                />
                {showEmoji && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: isMobile ? '-200px' : '220px',
                      zIndex: 999,
                    }}
                  >
                    <EmojiPicker onEmojiClick={handleMessageChange} />
                  </div>
                )}
              </Box>
            </Box>
          </Stack>
        </Paper>

        {/* queue section paper*/}
        {!isMobile && (
          <Paper
            sx={{
              width: '100%',
              maxWidth: 1250,
              height: '550px',
              bgcolor: '#181818',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              border: '2px solid rgb(53, 53, 53)',
              p: 0,
              margin: '0px auto 150px auto',
            }}
            elevation={0}
          >
            {/* Add to Queue Section (input + button) */}
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid #282828',
                bgcolor: '#1a1a1a',
              }}
            >
              <Stack
                spacing={1}
                direction={{ xs: 'column', sm: 'column' }}
                alignItems='center'
              >
                <TextField
                  fullWidth
                  placeholder='Paste YouTube URL or search...'
                  value={newVideoInput}
                  onChange={(e) => setNewVideoInput(e.target.value)}
                  size='small'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      backgroundColor: '#2a2a2a',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      '& fieldset': { borderColor: '#404040' },
                      '&:hover fieldset': { borderColor: '#1DB954' },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1DB954',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input': {
                      padding: '8px 12px',
                      '&::placeholder': {
                        color: '#b3b3b3',
                        opacity: 1,
                      },
                    },
                  }}
                />
                <Button
                  variant='contained'
                  startIcon={<Add />}
                  disabled={!newVideoInput?.length}
                  onClick={handleAddVideo}
                  sx={{
                    bgcolor: '#1DB954',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    width: '100%',
                    minWidth: 140,
                    '&:hover': {
                      bgcolor: '#1ed760',
                    },
                    '&:disabled': {
                      bgcolor: '#282828',
                      color: '#6a6a6a',
                    },
                  }}
                >
                  Add to Queue
                </Button>
              </Stack>
              <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                {queue.length > 0 && (
                  <DeleteModal
                    open={open}
                    setOpen={setOpen}
                    handleOpen={handleOpen}
                    handleClose={handleClose}
                    setQueue={setQueue}
                    setThumbnailImage={setThumbnailImage}
                    roomId={roomId}
                  />
                )}
              </Box>
            </Box>
            {/* Queue List parent*/}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                height: 350,
                '&::-webkit-scrollbar': {
                  width: '12px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#3e3e3e',
                  borderRadius: '6px',
                  border: '2px solid #181818',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#181818',
                },
              }}
            >
              {/* queue list items */}
              <Box sx={{ p: 2 }}>
                <DraggableQueue
                  queue={queue}
                  videoId={videoId}
                  setQueue={setQueue}
                  handlePlayQueueItem={handlePlayQueueItem}
                  handleDeleteVideo={handleDeleteVideo}
                  token={token}
                  socket={socket}
                  roomId={roomId}
                />
              </Box>
            </Box>
          </Paper>
        )}
        {/* Bottom Player */}
        <Paper
          sx={{
            width: '100%',
            position: 'fixed',
            bottom: '0',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.3)',
            zIndex: '1000',
          }}
        >
          <Card
            sx={{
              width: '100%',
              bgcolor: '#181818',
              color: 'white',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              borderRadius: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isMobile ? 'center' : 'space-between',
                flexDirection: isMobile ? 'column' : 'row',
                py: 2,
                px: '30px',
              }}
            >
              {/* Album Art and song info */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isMobile ? 'center' : 'space-between',
                  flexDirection: isMobile ? 'column' : 'row',
                  minWidth: '250px',
                }}
              >
                <CardMedia
                  component='img'
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 1,
                    mr: 2,
                  }}
                  image={
                    (!isQueueEmpty && thumbnailImage.songThumbnail) ||
                    'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTKilWmjIP0SBDq4yOV1Jq7vQWjQr1-w2Twzg&s'
                  }
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 500,
                      color: 'white',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {(!isQueueEmpty && thumbnailImage.author_name) ||
                      'No song selected'}
                  </Typography>
                  <Typography variant='caption' sx={{ color: '#b3b3b3' }}>
                    {!isQueueEmpty
                      ? 'Playing from Queue'
                      : 'Select a song to play'}
                  </Typography>
                </Box>
              </Box>

              {/* Center Controls */}
              <Stack
                direction='column'
                spacing={1}
                alignItems={'center'}
                sx={{ flex: 1 }}
              >
                {/* YouTube Player (hidden) */}
                <Paper
                  elevation={4}
                  sx={{
                    mb: 4,
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative',
                    visibility: 'hidden',
                  }}
                >
                  <YouTube
                    key={videoId}
                    videoId={videoId}
                    opts={{
                      ...opts,
                      width: '0',
                      height: '0',
                    }}
                    style={{ display: 'none' }}
                    onReady={onPlayerReady}
                    onStateChange={onPlayerStateChange}
                  />
                </Paper>

                {/* Play/Pause Button */}
                <IconButton
                  onClick={() =>
                    isPlaying
                      ? handlePlayPauseClick('Pause')
                      : handlePlayPauseClick('Play')
                  }
                  disabled={queue.length === 0}
                  sx={{
                    bgcolor: '#1DB954',
                    color: 'white',
                    width: 32,
                    height: 32,
                    '&:hover': {
                      bgcolor: '#1ed760',
                      transform: 'scale(1.04)',
                    },
                    '&:disabled': {
                      bgcolor: '#282828',
                      color: '#6a6a6a',
                    },
                  }}
                >
                  {isPlaying ? (
                    <Pause sx={{ fontSize: 20 }} />
                  ) : (
                    <PlayArrow sx={{ fontSize: 20 }} />
                  )}
                </IconButton>

                {/* Progress Bar */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: isMobile ? '350px' : '500px',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant='caption'
                    sx={{ color: '#b3b3b3', minWidth: 35, fontSize: '0.75rem' }}
                  >
                    {formatTime(currentTime)}
                  </Typography>
                  <Slider
                    disabled={isQueueEmpty}
                    value={
                      seeking ? undefined : (currentTime / duration) * 100 || 0
                    }
                    onChange={(e, val) => {
                      setSeeking(true)
                      const newTime = (val / 100) * duration
                      setCurrentTime(newTime)
                    }}
                    onChangeCommitted={(e, val) => {
                      const newTime = (val / 100) * duration
                      playerRef?.current?.seekTo(newTime, true)
                      handleSeek(newTime)
                      setSeeking(false)
                      if (roomId) {
                        socket.emit('seek', { time: newTime, roomId })
                      }
                    }}
                    sx={{
                      color: '#1DB954',
                      height: 4,
                      '& .MuiSlider-thumb': {
                        bgcolor: '#1DB954',
                        width: 12,
                        height: 12,
                        '&:hover': {
                          boxShadow: '0 0 0 8px rgba(29, 185, 84, 0.16)',
                        },
                      },
                      '& .MuiSlider-track': {
                        bgcolor: '#1DB954',
                        border: 'none',
                      },
                      '& .MuiSlider-rail': {
                        bgcolor: '#4f4f4f',
                      },
                    }}
                  />
                  <Typography
                    variant='caption'
                    sx={{ color: '#b3b3b3', minWidth: 35, fontSize: '0.75rem' }}
                  >
                    {formatTime(duration)}
                  </Typography>
                </Box>
              </Stack>

              {/* Right Controls */}
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ minWidth: '180px', justifyContent: 'flex-end' }}
              >
                <IconButton sx={{ color: '#b3b3b3' }}>
                  {isMobile ? (
                    <SwipeableEdgeDrawer
                      queue={queue}
                      videoId={videoId}
                      setQueue={setQueue}
                      handlePlayQueueItem={handlePlayQueueItem}
                      handleDeleteVideo={handleDeleteVideo}
                      newVideoInput={newVideoInput}
                      setNewVideoInput={setNewVideoInput}
                      handleAddVideo={handleAddVideo}
                    />
                  ) : (
                    <QueueMusic />
                  )}
                </IconButton>
                <VolumeUp sx={{ color: '#b3b3b3', fontSize: 20 }} />
                <Slider
                  value={volume}
                  onChange={handleVolumeChange}
                  sx={{
                    width: 80,
                    color: '#b3b3b3',
                    height: 4,
                    '& .MuiSlider-thumb': {
                      width: 12,
                      height: 12,
                      '&:hover': {
                        boxShadow: '0 0 0 8px rgba(179, 179, 179, 0.16)',
                      },
                    },
                    '& .MuiSlider-track': {
                      height: 4,
                    },
                    '& .MuiSlider-rail': {
                      height: 4,
                      color: '#404040',
                    },
                  }}
                />
                <Typography
                  variant='caption'
                  sx={{ minWidth: 30, color: '#b3b3b3', fontSize: '0.75rem' }}
                >
                  {volume}%
                </Typography>
              </Stack>
            </Box>
          </Card>
        </Paper>
      </Box>
    </>
  )
}
// delete modal
const styleDeleteModal = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: '#1e1e1e',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  color: 'white',
}

function DeleteModal({
  open,
  handleOpen,
  handleClose,
  setQueue,
  setThumbnailImage,
  roomId,
}) {
  return (
    <div>
      <Button
        onClick={handleOpen}
        variant='outlined'
        sx={{
          pr: 0.5,
          mt: '8px', //remove marigin
          '&:hover': {
            bgcolor: 'rgba(231, 13, 13, 0.3)',
            fontWeight: 'bold',
            boxShadow: '1px 1px 8px 8px rgba(231, 13, 13, 0.3)',
          },
        }}
        color='error'
      >
        Empty Queue
        <Delete sx={{ ml: 1 }} />
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box>
          <Box sx={styleDeleteModal}>
            <Typography
              id='modal-modal-title'
              variant='h6'
              component='h2'
              color='red'
            >
              Delete
            </Typography>
            <Typography id='modal-modal-description' sx={{ mt: 2 }}>
              Are you sure you want to delete the queue?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'end', mt: 4 }}>
              <Button onClick={handleClose} sx={{ mr: 2 }} variant='outlined'>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  socket.emit('updateQueue', { newQueue: [], token, roomId })
                  socket.emit('seek', { time: 0, token, roomId })
                  setQueue([])
                  setThumbnailImage({
                    songTitle: '',
                    songThumbnail: '',
                    author_name: '',
                  })
                  handleClose()
                }}
                variant='contained'
                color='error'
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </div>
  )
}

const drawerBleeding = 56

const Root = styled('div')(({ theme }) => ({
  height: '100%',
  backgroundColor: grey[100],
  ...theme.applyStyles('dark', {
    backgroundColor: (theme.vars || theme).palette.background.default,
  }),
}))

const StyledBox = styled('div')(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.applyStyles('dark', {
    backgroundColor: grey[800],
  }),
}))

const Puller = styled('div')(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: grey[300],
  borderRadius: 3,
  position: 'absolute',
  top: 8,
  left: 'calc(50% - 15px)',
  ...theme.applyStyles('dark', {
    backgroundColor: grey[900],
  }),
}))

function SwipeableEdgeDrawer(props) {
  const {
    window,
    queue,
    videoId,
    setQueue,
    handlePlayQueueItem,
    handleDeleteVideo,
    setNewVideoInput,
    newVideoInput,
    handleAddVideo,
  } = props
  const [open, setOpen] = useState(false)

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen)
  }

  // This is used only for the example
  const container =
    window !== undefined ? () => window().document.body : undefined

  return (
    <Root>
      <CssBaseline />
      <Global
        styles={{
          '.MuiDrawer-root > .MuiPaper-root': {
            height: `calc(50% - ${drawerBleeding}px)`,
            overflow: 'visible',
          },
        }}
      />
      {/* <Box sx={{ textAlign: 'center', pt: 1 }}>
        <Button onClick={toggleDrawer(true)}><QueueMusic/></Button>
      </Box> */}
      <SwipeableDrawer
        container={container}
        anchor='bottom'
        open={open}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        swipeAreaWidth={drawerBleeding}
        disableSwipeToOpen={false}
        keepMounted
      >
        <StyledBox
          sx={{
            position: 'absolute',
            top: -drawerBleeding,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            visibility: 'visible',
            right: 0,
            left: 0,
            bgcolor: 'rgba(0,0,0.7)',
          }}
        >
          <Puller />
          <Typography sx={{ p: 2, color: 'white' }}>
            {queue.length > 0 ? `Queue (${queue.length} Songs)` : 'Empty Queue'}
          </Typography>
        </StyledBox>
        <StyledBox
          sx={{
            px: 2,
            pb: 2,
            height: '100%',
            overflow: 'auto',
            bgcolor: 'black',
          }}
        >
          <Card
            elevation={3}
            sx={{
              mb: 2,
              borderRadius: 2,
              zIndex: 1,
              bgcolor: 'black',
              boxShadow: '0px 4px 4px rgba(188, 255, 188)',
            }}
          >
            <CardContent>
              <Typography
                variant='h6'
                color='white'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  zIndex: 1,
                  mb: 2,
                }}
              >
                <Add color='success' />
                Add to Queue
              </Typography>
              <Stack direction='row' spacing={2}>
                <TextField
                  fullWidth
                  placeholder='Enter YouTube URL or video ID'
                  value={newVideoInput}
                  onChange={(e) => setNewVideoInput(e.target.value)}
                  // variant='outlined'
                  size='medium'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      backgroundColor: '#282828', // Spotify input background
                      borderRadius: '4px',
                      '& fieldset': { borderColor: '#404040' },
                      '&:hover fieldset': { borderColor: '#1DB954' },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1DB954',
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': { padding: '12px 12px' },
                  }}
                />

                <Button
                  variant='contained'
                  onClick={handleAddVideo}
                  startIcon={<Add />}
                  disabled={!newVideoInput?.length}
                  color='success'
                  sx={{
                    minWidth: 140,
                    '&:disabled': {
                      opacity: 0.5,
                      border: '1px solid white',
                      color: 'white',
                    },
                    // border: '1px solid white',
                  }}
                >
                  Add
                </Button>
              </Stack>
            </CardContent>
          </Card>
          <DraggableQueue
            queue={queue}
            videoId={videoId}
            setQueue={setQueue}
            handlePlayQueueItem={handlePlayQueueItem}
            handleDeleteVideo={handleDeleteVideo}
            token={token}
            socket={socket}
          />
        </StyledBox>
      </SwipeableDrawer>
    </Root>
  )
}

SwipeableEdgeDrawer.propTypes = {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window: PropTypes.func,
}
